import React, { useState } from 'react';
import { X, CheckCircle, AlertTriangle, Search, Layers, User, ChevronRight, Stethoscope, Flame, Lock, HelpCircle } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';
import StatusDot from '../ui/StatusDot';
import { RealQRCode } from '../../utils/qr';

export default function QRScanView({ zones, employees, sites, companies, organizations, activeEvents, onScan, onClose, zoneManagementOnly = false, preselectedEmployee = null, preselectedVisitorCard = null }) {
  const [step, setStep] = useState(1);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(preselectedEmployee || null);
  const [action, setAction] = useState(null);
  const [emergencyType, setEmergencyType] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [zoneSearch, setZoneSearch] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [selectedVisitorCard] = useState(preselectedVisitorCard || null);

  const filteredZones = zones.filter(z => {
    // Access control: if employee is known, only show zones they can access
    const emp = preselectedEmployee || selectedEmployee;
    if (emp) {
      if (z.companyId === emp.companyId) {
        // Same company — always allowed
      } else {
        // Different company — only if employee has cross-company access AND same org
        if (!emp.allowedOtherCompanies) return false;
        const zoneCompany = companies.find(c => c.id === z.companyId);
        const empCompany = companies.find(c => c.id === emp.companyId);
        if (!zoneCompany || !empCompany || zoneCompany.orgId !== empCompany.orgId) return false;
      }
    }
    // Visitor card: restrict to allowed zones only (no site restriction — allowedZoneIds is the access control)
    if (selectedVisitorCard) {
      const allowed = selectedVisitorCard.allowedZoneIds || [];
      if (allowed.length > 0 && !allowed.includes(z.id)) return false;
    }
    const site = sites.find(s => s.id === z.siteId);
    const q = zoneSearch.toLowerCase();
    return !zoneSearch || z.name.toLowerCase().includes(q) || site?.name.toLowerCase().includes(q);
  });

  const filteredEmps = employees.filter(e => {
    // Determine if this employee can access the selected zone
    if (selectedZone) {
      const zoneCompany = companies.find(c => c.id === selectedZone.companyId);
      const empCompany = companies.find(c => c.id === e.companyId);
      if (e.companyId !== selectedZone.companyId) {
        // Different company — only allowed if employee has cross-company access AND same org
        if (!e.allowedOtherCompanies) return false;
        if (!zoneCompany || !empCompany || zoneCompany.orgId !== empCompany.orgId) return false;
      }
    }
    const q = empSearch.toLowerCase();
    return !empSearch || e.name.toLowerCase().includes(q) || e.employeeId.toLowerCase().includes(q);
  });

  function handleAction(act) {
    setAction(act);
    if (act === 'NEED_HELP') {
      // attempt GPS
      setGpsLoading(true);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
          () => setGpsLoading(false),
          { timeout: 5000 }
        );
      } else setGpsLoading(false);
    } else {
      handleSubmit(act, null, null);
    }
  }

  async function handleSubmit(act, eType, gpsData) {
    const a = act || action;
    if (!a || !selectedZone) return;
    if (!selectedEmployee && !selectedVisitorCard) return;
    try {
      await onScan({
        zoneId: selectedZone.id,
        ...(selectedVisitorCard
          ? { visitorCardId: selectedVisitorCard.id }
          : { employeeId: selectedEmployee.id }),
        action: a,
        emergencyType: eType,
        gps: gpsData,
      });
      setDone(true);
    } catch (err) {
      console.error('Scan failed:', err);
      alert(err?.response?.data?.error || 'Scan failed. Please try again.');
    }
  }

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 500,
  };
  const card = {
    background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '20px',
    width: '420px', maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  };

  if (done) {
    return (
      <div style={overlay}>
        <div style={{ ...card, alignItems: 'center', justifyContent: 'center', padding: '40px', textAlign: 'center' }}>
          <CheckCircle size={56} color={action === 'NEED_HELP' ? C.red : C.green} style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, marginBottom: '8px' }}>
            {action === 'NEED_HELP' ? 'Alert Sent' : action === 'EXIT_SITE' ? 'Out Zone Logged' : action === 'ENTER_ZONE' ? 'In Zone Logged' : 'Marked Safe'}
          </h3>
          <p style={{ fontSize: '14px', color: C.textSecondary, marginBottom: '24px' }}>
            {selectedVisitorCard?.visitorName || selectedVisitorCard?.cardNumber || selectedEmployee?.name} &mdash; {selectedZone?.name}
          </p>
          <button onClick={onClose} style={{ padding: '10px 28px', borderRadius: '10px', border: 'none', background: C.blue, color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '11px', color: zoneManagementOnly ? C.blue : C.textMuted, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{zoneManagementOnly ? 'Zone Management' : `Step ${step} of 2`}</div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>
              {step === 1 ? 'Select Zone' : action && action !== 'NEED_HELP' ? 'Action Selected' : (selectedEmployee || selectedVisitorCard) ? (zoneManagementOnly ? 'Zone Action' : 'Select Action') : 'Select Employee'}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={20} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Step 1: Select Zone */}
          {step === 1 && (
            <div>
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input value={zoneSearch} onChange={e => setZoneSearch(e.target.value)} placeholder="Search zones..."
                  style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px 8px 30px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }} />
              </div>
              {filteredZones.map(zone => {
                const site = sites.find(s => s.id === zone.siteId);
                return (
                  <div key={zone.id} onClick={() => { setSelectedZone(zone); setStep(2); }} style={{
                    background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px',
                    padding: '12px 14px', marginBottom: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                  }}>
                    <Layers size={16} color={C.blue} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{zone.name}</div>
                      <div style={{ fontSize: '11px', color: C.textMuted }}>{site?.name} &middot; Floor {zone.floor}</div>
                    </div>
                    <ChevronRight size={14} color={C.textMuted} style={{ marginLeft: 'auto' }} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2: Select Employee */}
          {step === 2 && !selectedEmployee && !selectedVisitorCard && (
            <div>
              <div style={{ background: C.surface, borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers size={14} color={C.blue} />
                <span style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 600 }}>{selectedZone?.name}</span>
                <span style={{ fontSize: '11px', color: C.textMuted, marginLeft: 'auto' }}>{sites.find(s=>s.id===selectedZone?.siteId)?.name}</span>
                <button onClick={() => { setSelectedZone(null); setStep(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={14} /></button>
              </div>
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employees..."
                  style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px 8px 30px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }} />
              </div>
              {filteredEmps.map(emp => (
                <div key={emp.id} onClick={() => setSelectedEmployee(emp)} style={{
                  background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px',
                  padding: '12px 14px', marginBottom: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <User size={16} color={C.textSecondary} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{emp.name}</div>
                    <div style={{ fontSize: '11px', color: C.textMuted }}>{emp.employeeId} &middot; {emp.department}</div>
                  </div>
                  <StatusDot status={emp.status} size={8} style={{ marginLeft: 'auto' }} />
                  <ChevronRight size={14} color={C.textMuted} />
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Action Buttons */}
          {step === 2 && (selectedEmployee || selectedVisitorCard) && !action && (
            <div>
              <div style={{ background: C.surface, borderRadius: '10px', padding: '10px 14px', marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '4px' }}>{selectedZone?.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={14} color={C.blue} />
                  <span style={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>
                    {selectedVisitorCard
                      ? (selectedVisitorCard.visitorName || selectedVisitorCard.cardNumber)
                      : selectedEmployee.name}
                  </span>
                  <span style={{ fontSize: '11px', color: C.textMuted }}>
                    {selectedVisitorCard ? selectedVisitorCard.cardNumber : selectedEmployee.employeeId}
                  </span>
                  {!selectedVisitorCard && (
                    <button onClick={() => setSelectedEmployee(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={14} /></button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {!zoneManagementOnly && (
                  <>
                    <button onClick={() => handleAction('SAFE')} style={{ padding: '18px', borderRadius: '12px', border: 'none', background: C.green, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <CheckCircle size={22} /> I AM SAFE
                    </button>
                    <button onClick={() => handleAction('NEED_HELP')} style={{ padding: '18px', borderRadius: '12px', border: 'none', background: C.red, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', animation: 'alertPulse 2s infinite' }}>
                      <AlertTriangle size={22} /> NEED HELP
                    </button>
                  </>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => handleAction('ENTER_ZONE')} style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${C.blue}44`, background: C.blue+'11', color: C.blue, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>In Zone</button>
                  <button onClick={() => handleAction('EXIT_SITE')} style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${C.border}`, background: C.surfaceElevated, color: C.textSecondary, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Out Zone</button>
                </div>
              </div>
            </div>
          )}

          {/* NEED_HELP: Emergency type selector */}
          {step === 2 && (selectedEmployee || selectedVisitorCard) && action === 'NEED_HELP' && !done && !zoneManagementOnly && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <AlertTriangle size={40} color={C.red} style={{ marginBottom: '10px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.red, margin: '0 0 4px' }}>Emergency Alert</h3>
                <p style={{ fontSize: '13px', color: C.textSecondary }}>Select the type of emergency</p>
                {gpsLoading && <p style={{ fontSize: '11px', color: C.blue, marginTop: '6px' }}>Locating GPS...</p>}
                {gps && <p style={{ fontSize: '11px', color: C.green, marginTop: '6px', fontFamily: 'JetBrains Mono, monospace' }}>GPS: {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}</p>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                {[['MEDICAL','Medical',C.pink,Stethoscope],['FIRE','Fire',C.orange,Flame],['SECURITY','Security',C.purple,Lock],['OTHER','Other',C.amber,HelpCircle]].map(([type,label,color,Icon]) => (
                  <button key={type} onClick={() => { setEmergencyType(type); handleSubmit('NEED_HELP', type, gps); }}
                    style={{ padding: '18px 10px', borderRadius: '12px', border: `2px solid ${color}`, background: color+'22', color, fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <Icon size={24} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
